import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useTheme } from '@mui/system';
import { Grid, Box, Typography, Button, Menu, MenuItem, Divider } from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { handleDeviceColumns } from 'utils/commonHelpers';
import MainCard from 'components/Cards/MainCard';
import GroupsTreeView from 'components/Management/DevicesFilter';
import ApiHelper from 'utils/apiHelper';
import EditDeviceColumnsVisibilityDialog from './EditDeviceColumnsVisibility';
import RightClickOptionsDialog from './RightClickOptionsDialog';
import { searchDevices } from 'redux/slices/search/searchActions';
import ReportFileExports from 'components/Reports/FileExports';

const ManagementDevices = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const gridRef = useRef();
    const [csvDownloadFileHeaders, setCsvDownloadFileHeaders] = useState([]);
    const { userInfo } = useSelector((state) => state.auth);
    const { devices } = useSelector((state) => state.devices);
    const [loading, setLoading] = useState(true);
    const [groupsData, setGroupsData] = useState({});
    const [deviceColumnsData, setDeviceColumnsData] = useState([]);
    const [showEditDeviceOrderColumns, setShowEditDeviceOrderColumns] = useState(false);
    const [columnDefs, setColumnDefs] = useState([]);
    const [showContextMenuDialog, setShowContextMenuDialog] = useState(false);
    const [rightClickOption, setRightClickOption] = useState('');
    const [selectedDevicesData, setSelectedDevicesData] = useState([]);
    const [selectedGroupId, setSelectedGroupId] = useState(0);
    const [contextMenu, setContextMenu] = useState(null);

    useEffect(() => {
        getDeviceColumns();
        getGroups();
    }, []);

    useEffect(() => {
        handleFilterDevicesByGroupId(selectedGroupId);
    }, [selectedGroupId]);

    const getGroups = () => {
        ApiHelper.get('/api/management/groups/')
            .then((res) => {
                setGroupsData(res.data);
            })
            .catch(() => toast.error('Failed to get the groups data'));
    };
    const getDeviceColumns = () => {
        ApiHelper.get('/api/management/device_columns/')
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(columns_order);
                setDeviceColumnsData(columns_visibility);
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch(() => toast.warn('There is device columns data'));
    };

    const handleFilterDevicesByGroupId = (id) => {
        setSelectedGroupId(id);
        setLoading(true);
        dispatch(searchDevices({ searchKey: '', groupId: id }))
            .then(() => setLoading(false))
            .catch(() => setLoading(false));
    };

    const handleCloseEditDeviceColumnsDialog = () => {
        setShowEditDeviceOrderColumns(false);
    };

    const handleCloseRightClickOptionsDialog = () => {
        setShowContextMenuDialog(false);
        handleFilterDevicesByGroupId(selectedGroupId);
    };

    const handleSubmitDeviceColumnsVisibility = (data) => {
        setShowEditDeviceOrderColumns(false);
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_column_visibility',
            data: data
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(columns_order);
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully updated.');
                const temp = [];
                columns_order.map((data) => {
                    temp.push({ label: data.headerName, key: data.field });
                });
                setCsvDownloadFileHeaders(temp);
            })
            .catch(() => {
                toast.error('Failed to update the device columns.');
            });
    };

    const handleChangeColumnOrder = (data) => {
        const { targetIndex, oldIndex } = data;
        const temp = columnDefs;
        const updatedVal = temp[oldIndex - 1];
        temp.splice(oldIndex - 1, 1);
        temp.splice(targetIndex - 1, 0, updatedVal);
        setColumnDefs(temp);
        const headers = [];
        temp.map((data) => {
            headers.push({ label: data.headerName, key: data.field });
        });
        setCsvDownloadFileHeaders(headers);
    };

    const handleSaveColumnOrders = () => {
        const columns_order = {};
        for (let i = 0; i < columnDefs.length; i++) {
            columns_order[columnDefs[i].field] = i + 1;
        }
        ApiHelper.put(`/api/management/device_columns/1/`, {
            status: 'device_columns_order',
            data: columns_order
        })
            .then((res) => {
                const [columns_order, columns_visibility] = handleDeviceColumns(res.data);
                setColumnDefs(columns_order);
                setDeviceColumnsData(columns_visibility);
                toast.success('Successfully saved.');
            })
            .catch(() => {
                toast.error('Failed to save the columns order');
            });
    };

    const onClickContextMenu = (e) => {
        setShowContextMenuDialog(true);
        handleCloseContextMenu();
        setRightClickOption(e.target.id);
    };

    const handleContextMenu = (event) => {
        event.preventDefault();
        if (selectedDevicesData.length === 0) {
            toast.warning('Please select the device');
            return true;
        }
        setContextMenu(contextMenu === null ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 } : null);
    };

    const handleCloseContextMenu = () => {
        setContextMenu(null);
    };

    const handleChangeRowSelection = (ids) => {
        const selectedIDs = new Set(ids);
        const selectedRows = devices.devices_data.filter((row) => selectedIDs.has(row.deviceid));
        setSelectedDevicesData(selectedRows);
    };

    if (!devices || !deviceColumnsData || !groupsData) {
        return null;
    } else {
        return (
            <MainCard title={`Device Management - ${groupsData['organization_data']?.name}`} cardHeaderShow={false}>
                <Grid container>
                    {devices.devices_data?.length > 0 && (
                        <Grid item xs={12} sx={{ textAlign: 'right' }}>
                            <Typography>
                                Showing {devices.devices_data.length} of {devices.total_devices} devices | {devices.category_name}:
                                {devices.group_name}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid
                            container
                            item
                            sx={{
                                background: `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary[200]})`,
                                padding: 1
                            }}
                        >
                            <Grid item sm={3} md={3}>
                                <Typography sx={{ color: '#FFFFFF', fontSize: '16px', marginTop: 1 }}>Managed Systems</Typography>
                            </Grid>
                            <Grid item sm={9} md={9} sx={{ textAlign: 'right' }}>
                                <Button
                                    sx={{ marginRight: '5px', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={handleSaveColumnOrders}
                                >
                                    Save Column Order
                                </Button>
                                <Button
                                    sx={{ marginRight: '5px', backgroundColor: '#FFFFFF', borderRadius: '6px' }}
                                    onClick={() => setShowEditDeviceOrderColumns(true)}
                                >
                                    Show/Hide Columns
                                </Button>
                                <ReportFileExports
                                    elementId="devices"
                                    downloadFileName="devices"
                                    downloadFileOption="csv"
                                    data={devices.devices_data}
                                    headers={csvDownloadFileHeaders}
                                />
                                {showEditDeviceOrderColumns && (
                                    <EditDeviceColumnsVisibilityDialog
                                        data={deviceColumnsData}
                                        onClose={handleCloseEditDeviceColumnsDialog}
                                        onSubmit={handleSubmitDeviceColumnsVisibility}
                                    />
                                )}
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item lg={2} md={3} sm={3} xs={12} sx={{ background: 'linear-gradient(to right, #fcc067, #f27d00)' }}>
                                <GroupsTreeView groupsData={groupsData} handleFilterGroups={handleFilterDevicesByGroupId} />
                            </Grid>
                            <Grid item lg={10} md={9} sm={9} xs={12}>
                                {loading === true ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '16px', fontWeight: 'bold' }}>
                                        Please wait...
                                    </Typography>
                                ) : devices.devices_data.length === 0 ? (
                                    <Typography sx={{ mt: 3, textAlign: 'center' }}>There is no devices data</Typography>
                                ) : (
                                    <Box
                                        id="devices"
                                        sx={{
                                            fontSize: '12px',
                                            '& .device-column-header': {
                                                color: 'black',
                                                fontWeight: 'bold'
                                            }
                                        }}
                                    >
                                        <DataGridPro
                                            columns={columnDefs}
                                            rows={devices.devices_data}
                                            rowHeight={25}
                                            ref={gridRef}
                                            getRowId={(row) => row.deviceid}
                                            pagination
                                            initialState={{
                                                pagination: {
                                                    paginationModel: {
                                                        pageSize: 25
                                                    }
                                                }
                                            }}
                                            pageSizeOptions={[25, 100, 500]}
                                            checkboxSelection
                                            disableColumnMenu
                                            onColumnOrderChange={handleChangeColumnOrder}
                                            slotProps={{
                                                row: {
                                                    onContextMenu: handleContextMenu,
                                                    style: { cursor: 'context-menu' }
                                                }
                                            }}
                                            onRowSelectionModelChange={handleChangeRowSelection}
                                        />
                                        <Menu
                                            open={contextMenu !== null}
                                            onClose={handleCloseContextMenu}
                                            anchorReference="anchorPosition"
                                            anchorPosition={
                                                contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                                            }
                                            componentsProps={{
                                                root: {
                                                    onContextMenu: (e) => {
                                                        e.preventDefault();
                                                        handleCloseContextMenu();
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem id="wake" onClick={onClickContextMenu}>
                                                Wake
                                            </MenuItem>
                                            <MenuItem id="sleep" onClick={onClickContextMenu}>
                                                Sleep
                                            </MenuItem>
                                            <MenuItem id="restart" onClick={onClickContextMenu}>
                                                Restart
                                            </MenuItem>
                                            <MenuItem id="shutdown" onClick={onClickContextMenu}>
                                                ShutDown
                                            </MenuItem>
                                            <MenuItem id="message" onClick={onClickContextMenu}>
                                                Send Message
                                            </MenuItem>
                                            <MenuItem id="group" onClick={onClickContextMenu}>
                                                Assign Group
                                            </MenuItem>
                                            <MenuItem id="policy" onClick={onClickContextMenu}>
                                                Assign Policy
                                            </MenuItem>
                                            <Divider />
                                            <MenuItem id="label" onClick={onClickContextMenu}>
                                                Assign Label
                                            </MenuItem>
                                            <MenuItem id="rename" onClick={onClickContextMenu}>
                                                Change Device Name
                                            </MenuItem>
                                            {userInfo.admin_permissions.root_admin && (
                                                <MenuItem id="setBaseline" onClick={onClickContextMenu}>
                                                    Set Baseline
                                                </MenuItem>
                                            )}
                                            <Divider />
                                            {userInfo.admin_permissions.root_admin && (
                                                <MenuItem id="getLogFile" onClick={onClickContextMenu}>
                                                    Get Log File
                                                </MenuItem>
                                            )}
                                            <MenuItem id="upgrade" onClick={onClickContextMenu}>
                                                Upgrade/No Upgrade
                                            </MenuItem>
                                            <MenuItem id="license" onClick={onClickContextMenu}>
                                                License/Unlicensed
                                            </MenuItem>
                                            {userInfo.admin_permissions.org_admin && (
                                                <MenuItem id="getTISData" onClick={onClickContextMenu}>
                                                    Get TIS Data
                                                </MenuItem>
                                            )}
                                        </Menu>
                                    </Box>
                                )}
                                {showContextMenuDialog && (
                                    <RightClickOptionsDialog
                                        option={rightClickOption}
                                        onClose={handleCloseRightClickOptionsDialog}
                                        devices={selectedDevicesData}
                                    />
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </MainCard>
        );
    }
};

export default ManagementDevices;
